import {
  UserProfitReportDocument,
  UserProfitReportExportDocument,
  type UserProfitReportExportQuery,
  type UserProfitReportExportQueryVariables,
  UserProfitReportGroupedByDocument,
  type UserProfitReportGroupedByQuery,
  type UserProfitReportGroupedByQueryVariables,
  type UserProfitReportQuery,
  type UserProfitReportQueryVariables,
  UserProfitReportStatsDocument,
  type UserProfitReportStatsQuery,
  type UserProfitReportStatsQueryVariables,
} from "@src/__generated__/graphql";
import type { UserProfitReportRow } from "@src/components/modules/reports/user-profit-report/components/tables";
import type { UserProfitReportGroupedByRow } from "@src/components/modules/reports/user-profit-report/components/tables/grouped-by";
import { UtilizationReportBaseStore } from "@src/components/modules/reports/utilization/stores/base-store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDate } from "@src/utils/dates";
import type { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";

export class UserProfitReportStore extends UtilizationReportBaseStore {
  readonly moduleKey: string;

  private statsFetcher = new FetchHelper<
    UserProfitReportStatsQuery,
    UserProfitReportStatsQueryVariables
  >(UserProfitReportStatsDocument);
  private reportFetcher = new FetchHelper<
    UserProfitReportQuery,
    UserProfitReportQueryVariables
  >(UserProfitReportDocument);
  private reportGroupedByFetcher = new FetchHelper<
    UserProfitReportGroupedByQuery,
    UserProfitReportGroupedByQueryVariables
  >(UserProfitReportGroupedByDocument);
  private exportFetcher = new FetchHelper<
    UserProfitReportExportQuery,
    UserProfitReportExportQueryVariables
  >(UserProfitReportExportDocument);

  @observable.ref
  tableStatsData:
    | UserProfitReportStatsQuery["utilizationReportSummary"]
    | undefined = undefined;

  @observable.ref
  tableData: UserProfitReportRow[] = [];

  @observable.ref
  tableGroupedByData: UserProfitReportGroupedByRow[] = [];

  constructor(appStore: AppStore) {
    const moduleKey = "user-profit-report";
    super(moduleKey, appStore, (pagination, groupBy) => {
      this.fetchStatsData();
      if (!groupBy) {
        this.fetchReportData(pagination);
      } else {
        this.fetchReportGroupedByData(pagination);
      }
    });
    makeObservable(this);
    this.moduleKey = moduleKey;
  }

  @computed
  get isStatsLoading() {
    return this.statsFetcher.isLoading.value;
  }

  @computed
  get isReportLoading() {
    return this.reportFetcher.isLoading.value;
  }

  @computed
  get isReportGroupedByLoading() {
    return this.reportGroupedByFetcher.isLoading.value;
  }

  @computed
  get isExportLoading() {
    return this.exportFetcher.isLoading.value;
  }

  @action.bound
  async fetchStatsData() {
    const [data, error] = await this.statsFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableStatsData = data.utilizationReportSummary;
  }

  @action.bound
  async fetchReportData(pagination: PaginationState) {
    const [data, error] = await this.reportFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      page: pagination.asParams.page,
      first: pagination.asParams.first,
      orderBy: this.orderBy.asWhereParam,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableData = data.utilizationReport.data.map((item, index) => ({
      id: `${item.user.id}-${index}`,
      ...item,
    }));
    this.pagination.setFromPaginatorInfo(data.utilizationReport.paginatorInfo);
  }

  @action.bound
  async fetchReportGroupedByData(pagination: PaginationState) {
    if (!this.groupBy) return;

    const [data, error] = await this.reportGroupedByFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
      page: pagination.asParams.page,
      first: pagination.asParams.first,
      groupBy: this.groupBy,
      groupOrderBy: this.orderByGrouped.asWhereParam,
      where: this.where.asWhereParam,
    });

    if (error) return;

    this.tableGroupedByData = data.utilizationReportGroups.data.map(
      (item, index) => ({
        id: `${item.group_title}-${index}`,
        group_title: item.group_title,
        report: item.report,
        items: item.items,
        __subRows: item.items.map(({ report, user }, index) => ({
          id: index.toString(),
          report,
          user,
          items: item.items,
        })),
      }),
    );
    this.pagination.setFromPaginatorInfo(
      data.utilizationReportGroups.paginatorInfo,
    );
  }

  @action.bound
  async exportReport() {
    const [data, error] = await this.exportFetcher.fetch({
      to: toApiDate(this.dateRange.end),
      from: toApiDate(this.dateRange.start),
      dataSource: this.dataSource,
    });

    if (error) return;

    window.open(data.utilizationReportExport, "_blank");
  }
}
